export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export const WEEKDAY_FULL_LABELS: Record<WeekdayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export const WEEKDAY_SHORT_LABELS: Record<WeekdayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

const WEEKDAY_KEY_SET = new Set<string>(WEEKDAY_KEYS);

export function hasHourValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

export function normalizeWeekday(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

/**
 * Fills any weekday missing from the collection so hours UIs can render
 * "Closed" instead of omitting Saturday/Sunday (or any other closed day).
 * Returns the original list unchanged when it is empty or has no recognizable
 * weekday names — we do not invent a week from no data.
 */
export function withClosedDays<T extends OfficeHourEntry>(hours: T[]): OfficeHourEntry[] {
  if (hours.length === 0) return [];

  const byDay = new Map<string, T>();
  const extras: T[] = [];

  for (const entry of hours) {
    if (!hasHourValue(entry.day)) continue;
    const key = normalizeWeekday(entry.day);
    if (WEEKDAY_KEY_SET.has(key)) {
      if (!byDay.has(key)) byDay.set(key, entry);
    } else {
      extras.push(entry);
    }
  }

  if (byDay.size === 0) return hours;

  const useFullLabels = [...byDay.values()].some(
    (entry) => (entry.day ?? '').trim().length > 3,
  );

  const week: OfficeHourEntry[] = WEEKDAY_KEYS.map((key) => {
    const existing = byDay.get(key);
    if (existing) return existing;
    return {
      day: useFullLabels ? WEEKDAY_FULL_LABELS[key] : WEEKDAY_SHORT_LABELS[key],
      open_time: null,
      close_time: null,
    };
  });

  return extras.length ? [...week, ...extras] : week;
}
